import { Button, Spinner, Text } from '@noorden-insurtech/alba-design-system';
import { formOptions } from '@tanstack/react-form';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import {
  findMorteCoverage,
  type PerfilDoGrupo,
  type TipoDePerfil,
} from '@/features/quotes/schemas/capital-global';
import { getMorteIndividualCapitalLimitsForRiskStep } from '@/features/quotes/schemas/capital-global/coverage-limits.util';
import { useCapitalGlobalCoveragesCatalog } from '@/features/quotes/hooks/capital-global/use-capital-global-coverages-catalog.hook';
import { useSubmitCapitalGlobalRiskBusiness } from '@/features/quotes/hooks/capital-global/use-submit-capital-global-risk-business.hook';
import {
  resolveVisibleRiskFieldError,
  type EntityRiskFieldErrors,
  type MapAttachValidationToRiskFieldErrorsResult,
} from '@/features/quotes/utils/map-attach-validation-to-risk-field-errors.util';
import { useQuoteForm } from '../../../hooks/common/use-quote-form.hook';
import { EscalonadoRiskFields } from './sections/escalonado-risk-fields.component';
import { UniformeRiskFields } from './sections/uniforme-risk-fields.component';
import {
  EntityRiskTabs,
  PerfilDoGrupoSection,
  RiskSummaryDisplay,
} from './sections/step-dados-do-risco.sections';
import {
  DEFAULT_RISK_VALUES,
  useStepDadosDoRiscoEntityTabs,
} from './hooks/use-step-dados-do-risco-entity-tabs.hook';
import { toaster } from '@/shared/ui/toaster.component';
import {
  formatIndividualCapitalRangeHint,
  parseNum,
  pickError,
  RISK_STEP_MAX_TOTAL_LIVES,
} from './utils/step-helpers.util';
import {
  buildCapitalOutOfRangeMessages,
  findEntitiesWithCapitalOutOfRange,
  getIndividualCapitalMaxAllowedError,
  getIndividualCapitalMinAllowedError,
  getIndividualCapitalMorteLimitError,
  getIndividualCapitalRangeError,
  getIndividualCapitalValidationError,
  getQuantidadeDeVidasValidationError,
  getRiskFieldsToValidate,
  getTotalLivesError,
  getUniformeIndividualCapitalError,
  isEntityRiskValuesValid,
  resolveCapitalBandFromTipoDePerfil,
  resolveEffectiveIndividualCapitalRange,
  sumLivesAcrossEntities,
} from './utils/validate-risk.util';

const formOpts = formOptions({
  defaultValues: {
    perfilDoGrupo: 'UNIFORME' as PerfilDoGrupo,

    // Campos Escalonado — Sócios/Diretores
    sdQuantidadeDeVidas: '',
    sdCapitalIndividual: '',

    // Campos Escalonado — Colaboradores
    colQuantidadeDeVidas: '',
    colCapitalIndividual: '',

    // Campos Uniforme
    tipoDePerfil: 'COLABORADORES' as TipoDePerfil,
    uniQuantidadeDeVidas: '',
    uniCapitalIndividual: '',
  },
});

type Props = {
  onNext: () => void;
  onBack: () => void;
  canGoNext?: boolean;
  canGoBack?: boolean;
  isViewMode?: boolean;
};

export function StepDadosDoRisco({
  onNext,
  onBack,
  canGoNext = true,
  canGoBack = true,
  isViewMode = false,
}: Props) {
  const form = useQuoteForm({ ...formOpts });
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const { coverageCatalog } = useCapitalGlobalCoveragesCatalog();
  const morteItem = useMemo(
    () => findMorteCoverage(coverageCatalog),
    [coverageCatalog]
  );
  const morteLimits = useMemo(
    () => getMorteIndividualCapitalLimitsForRiskStep(morteItem, undefined),
    [morteItem]
  );

  /*
   * Faixa anunciada no campo. Sai da MESMA função que o validador usa, então o
   * hint nunca promete um teto comercial que o catálogo da Morte corta mais
   * embaixo. No ESCALONADO as bandas são fixas; no UNIFORME a banda vem do Tipo
   * de Perfil e o hint é recomputado no render (ver abaixo).
   */
  const escalonadoCapitalHints = useMemo(
    () => ({
      partnersAndDirectors: formatIndividualCapitalRangeHint(
        resolveEffectiveIndividualCapitalRange(
          morteLimits,
          'partnersAndDirectors'
        )
      ),
      employees: formatIndividualCapitalRangeHint(
        resolveEffectiveIndividualCapitalRange(morteLimits, 'employees')
      ),
    }),
    [morteLimits]
  );

  const {
    entities,
    hasMultipleEntities,
    activeEntityId,
    handleTabChange,
    captureAllEntities,
  } = useStepDadosDoRiscoEntityTabs({ form, setSubmitAttempted });

  const [apiEntityErrors, setApiEntityErrors] = useState<
    Record<string, EntityRiskFieldErrors>
  >({});
  const [generalApiError, setGeneralApiError] = useState<string | undefined>();

  const handleAttachValidationError = useCallback(
    (result: MapAttachValidationToRiskFieldErrorsResult) => {
      setApiEntityErrors(result.entityErrors);
      setGeneralApiError(result.generalMessage?.trim() || undefined);
    },
    []
  );

  const handleInvalidEntityFromApi = useCallback(
    (newEntityId: string) => {
      handleTabChange(newEntityId, { preserveSubmitAttempted: true });
    },
    [handleTabChange]
  );

  const handleTabChangeWithReset = useCallback(
    (newEntityId: string, options?: { preserveSubmitAttempted?: boolean }) => {
      setApiEntityErrors({});
      setGeneralApiError(undefined);
      handleTabChange(newEntityId, options);
    },
    [handleTabChange]
  );

  const activeApiFieldErrors = apiEntityErrors[activeEntityId] ?? {};

  const { handleSubmit: submitToApi, isPending } =
    useSubmitCapitalGlobalRiskBusiness({
      onNext,
      captureAllEntities,
      entities,
      onInvalidEntity: handleInvalidEntityFromApi,
      onAttachValidationError: handleAttachValidationError,
    });

  async function handleSubmit() {
    setSubmitAttempted(true);

    /*
     * Capturado fora do ramo multi-estipulante: as regras comerciais (faixa de
     * Capital Individual e teto de 500 vidas somadas) valem também quando há um
     * único estipulante.
     */
    const allEntityValues = captureAllEntities();

    const capitalViolations = findEntitiesWithCapitalOutOfRange(
      allEntityValues,
      entities
    );
    if (capitalViolations.length > 0) {
      handleTabChangeWithReset(capitalViolations[0].entityId, {
        preserveSubmitAttempted: true,
      });
      toaster.create({
        type: 'error',
        title: 'Capital Individual fora do permitido',
        description:
          buildCapitalOutOfRangeMessages(capitalViolations).join('\n'),
      });
      return;
    }

    const totalLivesError = getTotalLivesError(
      sumLivesAcrossEntities(allEntityValues)
    );
    if (totalLivesError) {
      toaster.create({
        type: 'error',
        title: 'Limite de vidas excedido',
        description: totalLivesError,
      });
      return;
    }

    if (hasMultipleEntities) {
      const allValues = allEntityValues;

      for (const entity of entities) {
        const values = allValues[entity.id] ?? DEFAULT_RISK_VALUES;
        if (
          !isEntityRiskValuesValid(values, {
            morteItem,
            limits: morteLimits,
          })
        ) {
          handleTabChangeWithReset(entity.id, {
            preserveSubmitAttempted: true,
          });
          const fieldsToValidate = getRiskFieldsToValidate(
            values.perfilDoGrupo
          );
          await Promise.all(
            fieldsToValidate.map(field =>
              form.validateField(field as never, 'change')
            )
          );
          return;
        }
      }
    } else {
      const perfilDoGrupo = form.getFieldValue('perfilDoGrupo');
      const fieldsToValidate = getRiskFieldsToValidate(perfilDoGrupo);

      const results = await Promise.all(
        fieldsToValidate.map(field =>
          form.validateField(field as never, 'change')
        )
      );
      const hasErrors = results.some(r => Array.isArray(r) && r.length > 0);
      if (hasErrors) return;
    }

    await submitToApi();
  }

  const resolveFieldError = (
    formError: string | undefined,
    apiError: string | undefined,
    alwaysVisibleError?: string
  ) =>
    resolveVisibleRiskFieldError(formError, apiError, submitAttempted, {
      alwaysVisibleError,
    });

  const showGeneralApiError =
    submitAttempted &&
    generalApiError &&
    Object.keys(activeApiFieldErrors).length === 0;

  return (
    <form.Subscribe
      selector={state => state.values}
      children={values => {
        const isEscalonado = values.perfilDoGrupo === 'ESCALONADO';

        /*
         * Banda do UNIFORME. Recomputada a cada render, então trocar o Tipo de
         * Perfil já corrige o `alwaysVisibleError` na hora; o `onChangeListenTo`
         * do campo cuida do erro que ficou guardado no meta.
         */
        const uniformeCapitalBand = resolveCapitalBandFromTipoDePerfil(
          values.tipoDePerfil
        );
        const uniformeCapitalHint = formatIndividualCapitalRangeHint(
          resolveEffectiveIndividualCapitalRange(
            morteLimits,
            uniformeCapitalBand
          )
        );

        /*
         * Soma de vidas de TODAS as abas. `captureAllEntities` já sobrepõe a
         * entidade ativa com os valores vivos do formulário, então o contador
         * acompanha a digitação; é leitura pura de ref + form, mesma fonte que
         * o `handleSubmit` usa.
         */
        const totalLives = sumLivesAcrossEntities(captureAllEntities());
        const totalLivesError = getTotalLivesError(totalLives);

        const capitalSeguradoSd =
          parseNum(values.sdQuantidadeDeVidas) *
          parseNum(values.sdCapitalIndividual);
        const capitalSeguradoCol =
          parseNum(values.colQuantidadeDeVidas) *
          parseNum(values.colCapitalIndividual);
        const capitalTotalSegurado = isEscalonado
          ? capitalSeguradoSd + capitalSeguradoCol
          : parseNum(values.uniQuantidadeDeVidas) *
            parseNum(values.uniCapitalIndividual);

        const riskFormSections = (
          <>
            <form.AppField
              name='perfilDoGrupo'
              children={field => (
                <PerfilDoGrupoSection
                  value={field.state.value}
                  onChange={field.handleChange}
                  disabled={isViewMode}
                />
              )}
            />

            <div className='flex flex-col gap-6'>
              <Text className='text-lg font-bold text-alba-black-500'>
                Dados do Risco
              </Text>

              {isEscalonado ? (
                <form.AppField
                  name='sdQuantidadeDeVidas'
                  validators={{
                    onChange: ({ value }) =>
                      getQuantidadeDeVidasValidationError(value),
                  }}
                  children={sdQtdField => (
                    <form.AppField
                      name='sdCapitalIndividual'
                      validators={{
                        onChange: ({ value }) =>
                          getIndividualCapitalValidationError(value) ??
                          getIndividualCapitalMaxAllowedError(
                            value,
                            'partnersAndDirectors'
                          ) ??
                          getIndividualCapitalMinAllowedError(value) ??
                          getIndividualCapitalMorteLimitError(
                            value,
                            morteItem,
                            morteLimits,
                            'partnersAndDirectors'
                          ),
                      }}
                      children={sdCapField => (
                        <form.AppField
                          name='colQuantidadeDeVidas'
                          validators={{
                            onChange: ({ value }) =>
                              getQuantidadeDeVidasValidationError(value),
                          }}
                          children={colQtdField => (
                            <form.AppField
                              name='colCapitalIndividual'
                              validators={{
                                onChange: ({ value }) =>
                                  getIndividualCapitalValidationError(value) ??
                                  getIndividualCapitalMaxAllowedError(
                                    value,
                                    'employees'
                                  ) ??
                                  getIndividualCapitalMinAllowedError(value) ??
                                  getIndividualCapitalMorteLimitError(
                                    value,
                                    morteItem,
                                    morteLimits,
                                    'employees'
                                  ),
                              }}
                              children={colCapField => (
                                <EscalonadoRiskFields
                                  sdQuantidadeDeVidas={sdQtdField.state.value}
                                  onSdQuantidadeDeVidasChange={
                                    sdQtdField.handleChange
                                  }
                                  errorSdQuantidadeDeVidas={resolveFieldError(
                                    pickError(
                                      sdQtdField.state.meta,
                                      submitAttempted
                                    ),
                                    activeApiFieldErrors.sdQuantidadeDeVidas,
                                    totalLivesError
                                  )}
                                  sdCapitalIndividual={sdCapField.state.value}
                                  onSdCapitalIndividualChange={
                                    sdCapField.handleChange
                                  }
                                  errorSdCapitalIndividual={resolveFieldError(
                                    pickError(
                                      sdCapField.state.meta,
                                      submitAttempted
                                    ),
                                    activeApiFieldErrors.sdCapitalIndividual,
                                    getIndividualCapitalRangeError(
                                      sdCapField.state.value,
                                      'partnersAndDirectors'
                                    )
                                  )}
                                  sdCapitalIndividualHint={
                                    escalonadoCapitalHints.partnersAndDirectors
                                  }
                                  capitalSeguradoSd={capitalSeguradoSd}
                                  colQuantidadeDeVidas={colQtdField.state.value}
                                  onColQuantidadeDeVidasChange={
                                    colQtdField.handleChange
                                  }
                                  errorColQuantidadeDeVidas={resolveFieldError(
                                    pickError(
                                      colQtdField.state.meta,
                                      submitAttempted
                                    ),
                                    activeApiFieldErrors.colQuantidadeDeVidas,
                                    totalLivesError
                                  )}
                                  colCapitalIndividual={colCapField.state.value}
                                  onColCapitalIndividualChange={
                                    colCapField.handleChange
                                  }
                                  errorColCapitalIndividual={resolveFieldError(
                                    pickError(
                                      colCapField.state.meta,
                                      submitAttempted
                                    ),
                                    activeApiFieldErrors.colCapitalIndividual,
                                    getIndividualCapitalRangeError(
                                      colCapField.state.value,
                                      'employees'
                                    )
                                  )}
                                  colCapitalIndividualHint={
                                    escalonadoCapitalHints.employees
                                  }
                                  capitalSeguradoCol={capitalSeguradoCol}
                                  disabled={isViewMode}
                                />
                              )}
                            />
                          )}
                        />
                      )}
                    />
                  )}
                />
              ) : (
                <form.AppField
                  name='tipoDePerfil'
                  children={tipoField => (
                    <form.AppField
                      name='uniQuantidadeDeVidas'
                      validators={{
                        onChange: ({ value }) =>
                          getQuantidadeDeVidasValidationError(value),
                      }}
                      children={uniQtdField => (
                        <form.AppField
                          name='uniCapitalIndividual'
                          validators={{
                            /*
                             * O teto do UNIFORME depende do Tipo de Perfil, então
                             * trocar o radio precisa revalidar este campo — senão
                             * o erro guardado no meta sobrevive a uma troca que o
                             * tornou válido e aparece vermelho num campo correto.
                             */
                            onChangeListenTo: ['tipoDePerfil'],
                            /*
                             * O Tipo de Perfil vem do FORM, não do closure deste
                             * render: a revalidação acima roda síncrona dentro do
                             * `handleChange` do radio, antes de o React
                             * re-renderizar, então o validador executado ainda é o
                             * do render anterior. O store já tem o valor novo.
                             */
                            onChange: ({ value, fieldApi }) =>
                              getUniformeIndividualCapitalError(
                                value,
                                fieldApi.form.getFieldValue('tipoDePerfil'),
                                { morteItem, limits: morteLimits }
                              ),
                          }}
                          children={uniCapField => (
                            <UniformeRiskFields
                              tipoDePerfil={tipoField.state.value}
                              onTipoDePerfilChange={tipoField.handleChange}
                              quantidadeDeVidas={uniQtdField.state.value}
                              onQuantidadeDeVidasChange={
                                uniQtdField.handleChange
                              }
                              errorQuantidadeDeVidas={resolveFieldError(
                                pickError(
                                  uniQtdField.state.meta,
                                  submitAttempted
                                ),
                                activeApiFieldErrors.uniQuantidadeDeVidas,
                                totalLivesError
                              )}
                              capitalIndividual={uniCapField.state.value}
                              onCapitalIndividualChange={
                                uniCapField.handleChange
                              }
                              errorCapitalIndividual={resolveFieldError(
                                pickError(
                                  uniCapField.state.meta,
                                  submitAttempted
                                ),
                                activeApiFieldErrors.uniCapitalIndividual,
                                getIndividualCapitalRangeError(
                                  uniCapField.state.value,
                                  uniformeCapitalBand
                                )
                              )}
                              capitalIndividualHint={uniformeCapitalHint}
                              disabled={isViewMode}
                            />
                          )}
                        />
                      )}
                    />
                  )}
                />
              )}

              <RiskSummaryDisplay
                capitalTotalSegurado={capitalTotalSegurado}
                totalLives={totalLives}
                maxTotalLives={RISK_STEP_MAX_TOTAL_LIVES}
              />

              {showGeneralApiError && (
                <Text className='text-sm text-alba-error-red-500'>
                  {generalApiError}
                </Text>
              )}
            </div>
          </>
        );

        return (
          <div className='flex flex-col gap-8'>
            {hasMultipleEntities ? (
              <EntityRiskTabs
                entities={entities}
                activeEntityId={activeEntityId}
                onTabChange={handleTabChangeWithReset}
              >
                {riskFormSections}
              </EntityRiskTabs>
            ) : (
              riskFormSections
            )}

            <div className='flex flex-row justify-between items-center px-6 gap-6 self-stretch'>
              <Button
                variant='ghost'
                size='lg'
                className='flex items-center gap-2'
                onClick={onBack}
                disabled={isViewMode || !canGoBack}
              >
                <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
                Voltar
              </Button>
              <Button
                variant='primary'
                size='md'
                className='flex items-center gap-2 px-8 py-2 rounded-xl'
                onClick={handleSubmit}
                disabled={isViewMode || !canGoNext || isPending}
              >
                {isPending ? (
                  <Spinner size='sm' />
                ) : (
                  <>
                    Próximo
                    <ArrowRight className='w-6 h-6' strokeWidth={2} />
                  </>
                )}
              </Button>
            </div>
          </div>
        );
      }}
    />
  );
}
